import { type FC, useEffect, useState } from 'react';

import Minus from '@crehana/icons/outline/Minus';
import Plus4 from '@crehana/icons/Plus4';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';
import { clsx } from 'clsx';

interface CounterProps {
  disabled?: boolean;
  initialCount?: number;
  isDark?: boolean;
  limit?: number;
  minimum?: number;
  onChange?: (value: number) => void;
}

const { primaryMain, secondaryMain, grayDark, grayMain } = colors;

const Counter: FC<CounterProps> = ({
  disabled,
  initialCount = 0,
  isDark,
  limit,
  minimum = 0,
  onChange,
}) => {
  const [count, setCount] = useState(initialCount);
  const [isDecrementDisabled, setIsDecrementDisabled] = useState(
    disabled || false,
  );
  const [isIncrementDisabled, setIsIncrementDisabled] = useState(
    disabled || false,
  );

  useEffect(() => {
    setCount(initialCount);
  }, [initialCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    setIsDecrementDisabled(count === minimum);
    setIsIncrementDisabled(count === limit);
  }, [count]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    setIsIncrementDisabled(disabled || count == limit);
  }, [limit]);

  const handleCount = (count: number) => {
    onChange && onChange(count);
  };

  const squareButton =
    'flex justify-center items-center w-32 h-32 rounded-5 border-2 border-solid';

  const borderColor = isDark ? 'border-secondary-main' : 'border-primary-main';
  const borderDisabledColor = isDark ? 'border-gray-dark' : 'border-gray-main';

  const getBorderColor = (disabled: boolean) =>
    disabled ? borderDisabledColor : borderColor;

  const iconColor = isDark ? secondaryMain : primaryMain;
  const iconDisabledColor = isDark ? grayDark : grayMain;

  const getIconColor = (disabled: boolean) =>
    disabled ? iconDisabledColor : iconColor;

  return (
    <div className="flex justify-between items-center w-120">
      <button
        type="button"
        disabled={disabled || isDecrementDisabled}
        onClick={() => handleCount(count - 1)}
        className={clsx(squareButton, getBorderColor(isDecrementDisabled))}
      >
        <Minus size={14} color={getIconColor(isDecrementDisabled)} />
      </button>
      <span className="font-subtitle2">{count}</span>
      <button
        type="button"
        disabled={disabled || isIncrementDisabled}
        onClick={() => handleCount(count + 1)}
        className={clsx(squareButton, getBorderColor(isIncrementDisabled))}
      >
        <Plus4 size={14} color={getIconColor(isIncrementDisabled)} />
      </button>
    </div>
  );
};

export default Counter;
